import type { DyspatchEvent } from '@dyspatch/core';

/**
 * A run of consecutive, otherwise-identical events, read as one line (issue
 * #91). Before the sweep learned to seed its dedupe from the hold a task's
 * row already carried, a restart made it re-log an unchanged hold once per
 * queued task; the rows that burst wrote are still in the journal, honest and
 * unchanged, because deleting history is not this fix's job. This only
 * changes how a list of them reads.
 */
export interface CollapsedEvent extends DyspatchEvent {
  /** How many consecutive events this row stands for. 1 for an ordinary line. */
  count: number;
  /** The most recent of the collapsed events' timestamps. Equals `createdAt` when `count` is 1. */
  lastSeenAt: string;
}

/** Same decision, same task, same sentence: the sweep's own dedupe key, mirrored for the read side. */
function sameOccurrence(a: DyspatchEvent, b: DyspatchEvent): boolean {
  return a.kind === b.kind && a.taskId === b.taskId && a.message === b.message;
}

/**
 * Collapses consecutive runs of otherwise-identical events into one row apiece,
 * in whatever order `events` already is. Order-agnostic within a run:
 * `lastSeenAt` is the latest `createdAt` seen in the run, so this reads the
 * same whether the caller lists newest first (the hub's default) or oldest
 * first (a replay).
 */
export function collapseConsecutiveEvents(events: readonly DyspatchEvent[]): CollapsedEvent[] {
  const rows: CollapsedEvent[] = [];
  for (const event of events) {
    const row = rows.at(-1);
    if (row && sameOccurrence(row, event)) {
      row.count += 1;
      if (event.createdAt > row.lastSeenAt) row.lastSeenAt = event.createdAt;
      continue;
    }
    rows.push({ ...event, count: 1, lastSeenAt: event.createdAt });
  }
  return rows;
}
